import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface GlobalFormProtectionContextType {
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  trackValue: (key: string, value: any) => void;
  resetTracking: () => void;
  enableProtection: () => void;
  disableProtection: () => void;
}

const GlobalFormProtectionContext = createContext<GlobalFormProtectionContextType | undefined>(undefined);

export const useGlobalFormProtection = () => {
  const context = useContext(GlobalFormProtectionContext);
  if (!context) {
    // Return dummy functions if not in context
    return {
      isDirty: false,
      setIsDirty: () => {},
      trackValue: () => {},
      resetTracking: () => {},
      enableProtection: () => {},
      disableProtection: () => {},
    };
  }
  return context;
};

interface GlobalFormProtectionProps {
  children: React.ReactNode;
  enabled?: boolean;
  debugMode?: boolean;
}

export const GlobalFormProtection: React.FC<GlobalFormProtectionProps> = ({ 
  children, 
  enabled = true,
  debugMode = false 
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const trackedValues = useRef<Map<string, any>>(new Map());
  const initialValues = useRef<Map<string, any>>(new Map());
  const protectionEnabled = useRef(enabled);
  const hasInitialized = useRef(false);
  const isInModal = useRef(false);
  const modalCloseHandler = useRef<((e: Event) => void) | null>(null);

  // Track value changes
  const trackValue = useCallback((key: string, value: any) => {
    if (!protectionEnabled.current) return;

    // Initialize if first time seeing this key
    if (!hasInitialized.current || !initialValues.current.has(key)) {
      initialValues.current.set(key, value);
      trackedValues.current.set(key, value);
      
      if (debugMode) {
        console.log(`📝 Initial value for ${key}:`, value);
      }
      
      // After first initialization, start tracking
      if (hasInitialized.current) {
        checkDirtyState();
      } else {
        hasInitialized.current = true;
      }
      return;
    }

    // Update tracked value
    const oldValue = trackedValues.current.get(key);
    trackedValues.current.set(key, value);

    if (debugMode && oldValue !== value) {
      console.log(`🔄 Value changed for ${key}:`, { old: oldValue, new: value });
    }

    checkDirtyState();
  }, [debugMode]);

  // Check if form is dirty
  const checkDirtyState = useCallback(() => {
    let dirty = false;

    for (const [key, currentValue] of trackedValues.current.entries()) {
      const initialValue = initialValues.current.get(key);
      
      // Deep comparison for objects
      const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(initialValue);
      
      if (hasChanged) {
        dirty = true;
        if (debugMode) {
          console.log(`🚨 Dirty field: ${key}`, { 
            initial: initialValue, 
            current: currentValue 
          });
        }
      }
    }

    setIsDirty(dirty);
  }, [debugMode]);

  // Reset tracking
  const resetTracking = useCallback(() => {
    trackedValues.current.clear();
    initialValues.current.clear();
    hasInitialized.current = false;
    setIsDirty(false);
    if (debugMode) {
      console.log('🧹 Form tracking reset');
    }
  }, [debugMode]);

  // Enable/disable protection
  const enableProtection = useCallback(() => {
    protectionEnabled.current = true;
    if (debugMode) {
      console.log('✅ Form protection enabled');
    }
  }, [debugMode]);

  const disableProtection = useCallback(() => {
    protectionEnabled.current = false;
    setIsDirty(false);
    if (debugMode) {
      console.log('❌ Form protection disabled');
    }
  }, [debugMode]);

  // Check if we're in a modal with actual form inputs (not just filters)
  const checkIfInModal = useCallback(() => {
    const modals = document.querySelectorAll(
      '[role="dialog"], [role="presentation"], .MuiDialog-root, .MuiModal-root, .MuiDrawer-root'
    );
    
    for (const modal of modals) {
      // Check if modal is visible
      const style = window.getComputedStyle(modal);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        // Check if there's a form or actual inputs (not just filter controls)
        const hasForm = modal.querySelector('form');
        const dialogContent = modal.querySelector('.MuiDialogContent-root, [role="dialog"]');
        
        if (hasForm || dialogContent) {
          // Only consider it a "form modal" if it has saveable content
          const hasSubmitButton = modal.querySelector('[type="submit"], button[class*="save"], button[class*="Save"], button[class*="submit"], button[class*="Submit"]');
          const hasDialogActions = modal.querySelector('.MuiDialogActions-root');
          
          if (hasForm || (hasDialogActions && hasSubmitButton)) {
            if (debugMode) {
              console.log('🔍 Found active modal with form inputs');
            }
            return true;
          }
        }
      }
    }
    return false;
  }, [debugMode]);

  // Monitor ALL input changes using MutationObserver and event delegation
  useEffect(() => {
    if (!enabled) return;

    let inputTimeout: NodeJS.Timeout;
    const inputCache = new Map<Element, string>();

    // Check if element should be tracked (skip UI controls)
    const shouldTrackElement = (element: Element): boolean => {
      // Skip if element has data-no-track attribute
      if (element.hasAttribute('data-no-track')) return false;
      
      // Skip filter controls
      const filterParent = element.closest('[data-filter], [data-role="filter"], .MuiTablePagination-root');
      if (filterParent) return false;
      
      // Skip search bars that aren't in forms
      const form = element.closest('form');
      const dialog = element.closest('[role="dialog"], .MuiDialog-root, .MuiModal-root');
      if (!form && !dialog) {
        // It's a standalone control, likely a filter
        const isSearch = element.getAttribute('type') === 'search' || 
                        element.getAttribute('placeholder')?.toLowerCase().includes('search');
        const isFilter = element.getAttribute('name')?.toLowerCase().includes('filter') ||
                        element.getAttribute('id')?.toLowerCase().includes('filter');
        if (isSearch || isFilter) return false;
      }
      
      // Skip pagination controls
      if (element.closest('.MuiPagination-root, .MuiTablePagination-root')) return false;
      
      // Skip table sorting/filter controls
      if (element.closest('thead, .MuiTableHead-root')) return false;
      
      // Skip toolbar controls unless in a form
      if (!form && element.closest('.MuiToolbar-root, [role="toolbar"]')) return false;
      
      return true;
    };
    
    // Function to extract value from any input element
    const getElementValue = (element: Element): any => {
      if (element instanceof HTMLInputElement) {
        if (element.type === 'checkbox') return element.checked;
        if (element.type === 'radio') return element.checked ? element.value : null;
        return element.value;
      }
      if (element instanceof HTMLTextAreaElement) {
        return element.value;
      }
      if (element instanceof HTMLSelectElement) {
        return element.value;
      }
      // For Material-UI components, try to find the actual input
      const input = element.querySelector('input, textarea, select');
      if (input) {
        return getElementValue(input);
      }
      return null;
    };

    // Monitor all input changes
    const handleAnyChange = (e: Event) => {
      const target = e.target as Element;
      
      // Get the actual input element
      let inputElement = target;
      if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        // Try to find the actual input within the component
        const input = target.closest('.MuiTextField-root, .MuiSelect-root, .MuiFormControl-root');
        if (input) {
          const actualInput = input.querySelector('input, textarea, select');
          if (actualInput) {
            inputElement = actualInput;
          }
        }
      }
      
      // Check if we should track this element
      if (!shouldTrackElement(inputElement)) {
        return; // Skip tracking this element
      }

      // Get identifier
      const id = (inputElement as any).id || 
                 (inputElement as any).name || 
                 inputElement.getAttribute('aria-label') ||
                 inputElement.getAttribute('data-testid') ||
                 `field_${Array.from(document.querySelectorAll('input, textarea, select')).indexOf(inputElement)}`;

      // Get value
      const value = getElementValue(inputElement);
      
      if (value !== null && value !== undefined) {
        // Debounce tracking
        clearTimeout(inputTimeout);
        inputTimeout = setTimeout(() => {
          trackValue(id, value);
        }, 100);
      }
    };

    // Listen to all possible change events at document level
    const events = ['input', 'change', 'blur', 'focusout'];
    events.forEach(event => {
      document.addEventListener(event, handleAnyChange, true);
    });

    // Also monitor React's synthetic events by intercepting setState
    const monitorReactChanges = () => {
      // Check all inputs periodically for changes
      const checkInterval = setInterval(() => {
        if (!protectionEnabled.current) return;

        const allInputs = document.querySelectorAll('input, textarea, select');
        allInputs.forEach(input => {
          // Skip if we shouldn't track this element
          if (!shouldTrackElement(input)) {
            return;
          }
          
          const id = (input as any).id || 
                     (input as any).name || 
                     `field_${Array.from(allInputs).indexOf(input)}`;
          const currentValue = getElementValue(input);
          const cachedValue = inputCache.get(input);
          
          if (currentValue !== cachedValue) {
            inputCache.set(input, currentValue);
            if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
              trackValue(id, currentValue);
            }
          }
        });
        
        // Update modal state
        isInModal.current = checkIfInModal();
      }, 500);

      return () => clearInterval(checkInterval);
    };

    const cleanupReactMonitor = monitorReactChanges();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleAnyChange, true);
      });
      clearTimeout(inputTimeout);
      cleanupReactMonitor();
    };
  }, [enabled, trackValue, checkIfInModal]);

  // Handle browser navigation
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && protectionEnabled.current) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, enabled]);

  // Intercept navigation attempts via popstate
  useEffect(() => {
    if (!enabled) return;

    const handlePopState = (e: PopStateEvent) => {
      if (isDirty && protectionEnabled.current) {
        // Show dialog
        setShowDialog(true);
        // Store the navigation we're trying to do
        setPendingNavigation(() => () => {
          window.history.go(1); // Continue with navigation
        });
        
        // Prevent the navigation
        window.history.pushState(null, '', window.location.href);
        e.preventDefault();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to enable back button detection
    window.history.pushState(null, '', window.location.href);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, enabled]);

  // Intercept link clicks and modal closes
  useEffect(() => {
    if (!enabled) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as Element;

      // IMPORTANT: Don't intercept clicks on the exit confirmation dialog itself
      const isExitConfirmationDialog = target.closest('[data-exit-confirmation="true"]') ||
                                       target.closest('[aria-labelledby="exit-dialog-title"]') ||
                                       target.closest('#exit-dialog-title')?.parentElement?.parentElement ||
                                       (target.textContent?.includes('Stay on Page') ||
                                        target.textContent?.includes('Leave Without Saving'));

      if (isExitConfirmationDialog) {
        // Allow the exit confirmation dialog buttons to work
        if (debugMode) {
          console.log('✅ Allowing click on exit confirmation dialog', target);
        }
        return;
      }

      // Check if it's a modal backdrop or close button
      const isBackdrop = target.classList.contains('MuiBackdrop-root') ||
                         target.classList.contains('MuiModal-backdrop');
      const isCloseButton = target.closest('[aria-label*="close"], [aria-label*="Close"], .MuiDialogTitle-root button, [data-testid*="close"]');
      const isDialogAction = target.closest('.MuiDialogActions-root button');

      // Check if this is a cancel/close action (not save/submit)
      const isCancelAction = isDialogAction && (
        target.textContent?.toLowerCase().includes('cancel') ||
        target.textContent?.toLowerCase().includes('close') ||
        (!target.textContent?.toLowerCase().includes('save') &&
         !target.textContent?.toLowerCase().includes('submit') &&
         !target.closest('[type="submit"]'))
      );

      // If we're in a modal and trying to close it
      if ((isBackdrop || isCloseButton || isCancelAction) &&
          isInModal.current && isDirty && protectionEnabled.current) {
        
        if (debugMode) {
          console.log('🚫 Blocking modal close attempt', {
            isBackdrop,
            isCloseButton,
            isDialogAction,
            isDirty
          });
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        setShowDialog(true);
        setPendingNavigation(() => () => {
          // Find and close the modal
          const modal = document.querySelector('[role="dialog"], [role="presentation"]');
          if (modal) {
            // Trigger close by dispatching escape key
            const escEvent = new KeyboardEvent('keydown', {
              key: 'Escape',
              code: 'Escape',
              keyCode: 27,
              bubbles: true
            });
            modal.dispatchEvent(escEvent);
            
            // Also try clicking the backdrop
            const backdrop = document.querySelector('.MuiBackdrop-root');
            if (backdrop) {
              (backdrop as HTMLElement).click();
            }
          }
        });
        
        return;
      }
      
      // Check if it's a link or button that would navigate
      const link = target.closest('a[href], [role="link"], .MuiListItem-button, .MuiTab-root');
      
      if (link && isDirty && protectionEnabled.current && !isInModal.current) {
        const href = link.getAttribute('href');
        
        // Check if it's an internal navigation
        if (href && !href.startsWith('http') && !href.startsWith('mailto:')) {
          e.preventDefault();
          e.stopPropagation();
          
          setShowDialog(true);
          setPendingNavigation(() => () => {
            if (href) {
              window.location.href = href;
            } else {
              // For buttons that navigate programmatically
              (link as HTMLElement).click();
            }
          });
        }
      }
    };

    // Intercept Escape key for modals
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isInModal.current && isDirty && protectionEnabled.current) {
        if (debugMode) {
          console.log('🚫 Blocking Escape key in modal');
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        setShowDialog(true);
        setPendingNavigation(() => () => {
          // Re-dispatch the escape key after confirmation
          const newEscEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true
          });
          document.dispatchEvent(newEscEvent);
        });
      }
    };

    // Use capture phase to intercept before React
    document.addEventListener('click', handleLinkClick, true);
    document.addEventListener('keydown', handleEscape, true);
    
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isDirty, enabled, debugMode]);

  const handleConfirmLeave = () => {
    setShowDialog(false);
    disableProtection();
    
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelLeave = () => {
    setShowDialog(false);
    setPendingNavigation(null);
  };

  // Log state in debug mode
  useEffect(() => {
    if (debugMode) {
      console.log('🔍 Form Protection State:', {
        isDirty,
        enabled: protectionEnabled.current,
        trackedFields: Array.from(trackedValues.current.keys()),
        values: Object.fromEntries(trackedValues.current.entries())
      });
    }
  }, [isDirty, debugMode]);

  return (
    <GlobalFormProtectionContext.Provider
      value={{
        isDirty,
        setIsDirty,
        trackValue,
        resetTracking,
        enableProtection,
        disableProtection
      }}
    >
      {children}
      
      <Dialog
        open={showDialog}
        onClose={handleCancelLeave}
        maxWidth="sm"
        fullWidth
        aria-labelledby="exit-dialog-title"
        aria-describedby="exit-dialog-description"
        data-exit-confirmation="true"
      >
        <DialogTitle id="exit-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-dialog-description">
            You have unsaved changes that will be lost if you leave this page.
            Are you sure you want to continue without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave} color="primary" variant="outlined">
            Stay on Page
          </Button>
          <Button onClick={handleConfirmLeave} color="warning" variant="contained">
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </GlobalFormProtectionContext.Provider>
  );
};